import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface UseOtecAxios {
  method: string;
  endpoint: string;
  responseType?: any;
  adapters?: {
    send?: {
      adapter: (data: any) => any;
    };
    receive?: {
      adapter: (response: any) => any;
    };
  };
}

interface OtecState {
  data: any;
  loading: boolean;
  error: any;
  status: number | null;
}

export const useOtecAxios = ({ 
  method, 
  endpoint, 
  responseType = 'json', 
  adapters 
}: UseOtecAxios) => {
  const API_BASE_URL = process.env.REACT_APP_WEB_SERVICE_ACTIVIDADES || 'http://activitats.test/webServiceAdmin';
  const { auth } = useSelector((state: RootState) => state.auth);
  const token = auth.token;
  const navigate = useNavigate();
  const isMounted = useRef(true);

  const [state, setState] = useState<OtecState>({ 
    data: null, 
    loading: false, 
    error: null, 
    status: null 
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchData = (config?: {
    id?: string | number;
    body?: any;
    params?: any;
  }) => {
    if (!isMounted.current) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    axios({
      method,
      url: `${API_BASE_URL}/${endpoint}${config?.id ? `/${config.id}` : ''}`,
      headers: {
        'TOKEN-EBCN': token ? token : '',
        'Content-Type': 'application/json',
      },
      responseType,
      data: config?.body ? 
        (adapters?.send?.adapter ? adapters.send.adapter(config.body) : config.body) : 
        undefined,
      params: config?.params
    })
    .then(response => {
        const adaptedData = adapters?.receive?.adapter ? 
          adapters.receive.adapter(response) : 
          response.data;
        
        setState({
          data: adaptedData,
          loading: false,
          error: null,
          status: response.status
        });
    })
    .catch(error => {
      if (isMounted.current) {
        setState({
          data: null,
          loading: false,
          error: error?.response?.data,
          status: error?.response?.status
        });
      }
    });
  };

  const resetState = () => {
    setState({ data: null, loading: false, error: null, status: null });
  };

  return { fetchData, ...state, resetState };
};